import React, { useMemo, useState } from "react";
import Card from "@mui/material/Card";
import Box from "@mui/material/Box";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import CardContent from "@mui/material/CardContent";
import CardHeader from "@mui/material/CardHeader";
import Stack from "@mui/material/Stack";
import CircularProgress from "@mui/material/CircularProgress";
import TextField from "@mui/material/TextField";
import { useNavigate, useParams } from "react-router-dom";
import { EmptyDetailCard } from "../core/EmptyDetailCard";
import { CircularProgressSize } from "../theme";
import { TimezoneSelect } from "../core/TimezoneDropDown";
import { SelectConventionDialogForSession } from "../core/SelectConventionCard";
import { SelectGamesDialog } from "../core/SelectGamesDialog";
import { SelectPublishersDialog } from "../core/SelectPublishersDialog";
import { useMyProfile, useUserSessions, useUpdateSession } from "@seabrookstudios/pitch2table";
import { formatSessionDate } from "@seabrookstudios/pitch2table-core";
import { EditableDate } from "../core/EditableDate";
import { EditableListItemUrl } from "../core/EditableListItemUrl";
import { SessionRelevantTimezones } from "./SessionRelevantTimezones";
import { SessionTypeSelect } from "./SessionTypeSelect";
import Delete from "@mui/icons-material/Delete";
import IconButton from "@mui/material/IconButton";
import { Path } from "../Path";

/**
 * @param {object} props
 * @param {import("@seabrookstudios/pitch2table-core").SessionId} props.sessionId
 */
const DeleteButton = ({ sessionId }) => {
  const navigate = useNavigate();
  const deleteSession = useUpdateSession(sessionId);

  const onDeleteHandler = async () => {
    await deleteSession.mutateAsync({
      type: undefined,
      date: undefined,
      timezone: undefined,
      duration: undefined,
      conventionId: undefined,
      gameIds: undefined,
      publisherIds: undefined,
      meetingRoomUrl: undefined,
      deleted: true,
    });

    navigate(Path.events().none());
  };
  return (
    <IconButton onClick={onDeleteHandler}>
      {deleteSession.isPending ? <CircularProgress size={CircularProgressSize} /> : <Delete />}
    </IconButton>
  );
};

/**
 * @returns {import("@seabrookstudios/pitch2table-core").SessionId}
 */
const useSessionIdFromRoute = () => {
  return /** @type {import("@seabrookstudios/pitch2table-core").SessionId} */ (useParams().sessionId || "");
};

export const SessionFromRoute = () => {
  const sessionId = useSessionIdFromRoute();
  const { data: sessions = [], isLoading } = useUserSessions();
  const { data: profile } = useMyProfile();

  const session = useMemo(() => sessions.find((s) => s.id === sessionId), [sessions, sessionId]);

  const updateSession = useUpdateSession(sessionId);

  const [localDuration, setLocalDuration] = useState(() => (session?.duration ? session.duration : 60));

  if (isLoading || !profile) {
    return (
      <Box>
        <Card elevation={1} square={false}>
          <Box p={2}>
            <CircularProgress size={CircularProgressSize} />
          </Box>
        </Card>
      </Box>
    );
  }

  if (!session) {
    return <EmptyDetailCard text="Select a meeting from the list on the left." />;
  }

  const dateMillis = session.date || Date.now();

  /**
   * @param {string} timezone
   */
  const onChangeTimezone = async (timezone) => {
    await updateSession.mutateAsync({
      timezone,
      type: undefined,
      date: undefined,
      duration: undefined,
      conventionId: undefined,
      gameIds: undefined,
      publisherIds: undefined,
      meetingRoomUrl: undefined,
      deleted: undefined,
    });
  };

  /**
   * @param {number} newDateMillis
   */
  const onChangeDate = async (newDateMillis) => {
    if (!newDateMillis) {
      return;
    }
    await updateSession.mutateAsync({
      date: newDateMillis,
      type: undefined,
      timezone: undefined,
      duration: undefined,
      conventionId: undefined,
      gameIds: undefined,
      publisherIds: undefined,
      meetingRoomUrl: undefined,
      deleted: undefined,
    });
  };

  const onBlurDuration = async () => {
    if (localDuration && localDuration !== session.duration) {
      await updateSession.mutateAsync({
        duration: localDuration,
        type: undefined,
        timezone: undefined,
        date: undefined,
        conventionId: undefined,
        gameIds: undefined,
        publisherIds: undefined,
        meetingRoomUrl: undefined,
        deleted: undefined,
      });
    }
  };

  const onChangeDuration = (e) => {
    const value = parseInt(e.target.value, 10);
    setLocalDuration(Number.isNaN(value) ? 0 : value);
  };

  /**
   * @param {"Pitching"|"PlayTesting"} type
   */
  const onChangeType = async (type) => {
    await updateSession.mutateAsync({
      type,
      timezone: undefined,
      date: undefined,
      duration: undefined,
      conventionId: undefined,
      gameIds: undefined,
      publisherIds: undefined,
      meetingRoomUrl: undefined,
      deleted: undefined,
    });
  };

  /**
   * @param {import("@seabrookstudios/pitch2table-core").ConventionId|null} conventionId
   */
  const onChangeConvention = async (conventionId) => {
    await updateSession.mutateAsync({
      conventionId,
      type: undefined,
      timezone: undefined,
      date: undefined,
      duration: undefined,
      gameIds: undefined,
      publisherIds: undefined,
      meetingRoomUrl: undefined,
      deleted: undefined,
    });
  };

  /**
   * @param {import("@seabrookstudios/types").GameId[]} gameIds
   */
  const onChangeGames = async (gameIds) => {
    await updateSession.mutateAsync({
      gameIds,
      type: undefined,
      timezone: undefined,
      date: undefined,
      duration: undefined,
      conventionId: undefined,
      publisherIds: undefined,
      meetingRoomUrl: undefined,
      deleted: undefined,
    });
  };

  /**
   * @param {import("@seabrookstudios/pitch2table-core").PublisherId[]} publisherIds
   */
  const onChangePublishers = async (publisherIds) => {
    await updateSession.mutateAsync({
      publisherIds,
      type: undefined,
      timezone: undefined,
      date: undefined,
      duration: undefined,
      conventionId: undefined,
      gameIds: undefined,
      meetingRoomUrl: undefined,
      deleted: undefined,
    });
  };

  /**
   * @param {import("@seabrookstudios/types").Url} meetingRoomUrl
   */
  const onChangeMeetingRoomUrl = async (meetingRoomUrl) => {
    await updateSession.mutateAsync({
      meetingRoomUrl,
      type: undefined,
      timezone: undefined,
      date: undefined,
      duration: undefined,
      conventionId: undefined,
      gameIds: undefined,
      publisherIds: undefined,
      deleted: undefined,
    });
  };

  return (
    <Box>
      <Card elevation={1} square={false}>
        <CardHeader
          title={session.type === "Pitching" ? "Pitch Meeting" : "Play Testing Session"}
          action={<DeleteButton sessionId={sessionId} />}
        />
        <CardContent>
          <List>
            <ListItem>
              <SessionTypeSelect value={session.type} onChange={onChangeType} />
            </ListItem>
            <ListItem>
              <TimezoneSelect value={session.timezone} onChange={onChangeTimezone} title="Timezone of meeting" />
            </ListItem>
            <ListItem>
              <Stack direction="column" flex={1} p={1} gap={1}>
                <Box flex={1}>
                  <EditableDate
                    title="When"
                    mode="datetime"
                    timestamp={dateMillis}
                    formatter={(newDate) => formatSessionDate(newDate, session.timezone)}
                    onSave={onChangeDate}
                    isBusy={updateSession.isPending}
                    timezone={session.timezone}
                  />
                </Box>
                <SessionRelevantTimezones
                  publisherIds={session.publisherIds}
                  gameIds={session.gameIds}
                  meetingTimezone={session.timezone || "UTC"}
                  meetingDateMillis={dateMillis}
                  viewerTimezone={profile.location?.timezone}
                />
              </Stack>
            </ListItem>
            <ListItem>
              <TextField
                fullWidth
                type="number"
                label="Duration (minutes)"
                value={localDuration}
                onChange={onChangeDuration}
                onBlur={onBlurDuration}
              />
            </ListItem>
            <EditableListItemUrl
              title="Meeting Room URL"
              text={session.meetingRoomUrl}
              helperText="Enter the URL for the meeting room"
              onSave={onChangeMeetingRoomUrl}
              isBusy={updateSession.isPending}
              noIcon
            />
            <ListItem>
              <SelectConventionDialogForSession
                value={session.conventionId ? [session.conventionId] : []}
                onChange={onChangeConvention}
                sessionDate={dateMillis}
              />
            </ListItem>
            <ListItem>
              <SelectGamesDialog
                value={session.gameIds}
                onChange={onChangeGames}
                defaultSelectedStatuses={["PitchingNow"]}
              />
            </ListItem>
            <ListItem>
              <SelectPublishersDialog value={session.publisherIds} onChange={onChangePublishers} />
            </ListItem>
          </List>
        </CardContent>
      </Card>
    </Box>
  );
};
